import { useState, useContext, useEffect, useMemo } from "react"

import { Button, Typography, TextField, Checkbox, FormControlLabel, MenuItem } from "@mui/material"
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import { enqueueSnackbar } from "notistack"

import { AppContext } from "/providers/app-context"
import { applyForMonthlyPlan } from '/providers/studio-api'
import PopupShell from '../editor/popup-shell'
import ActionErrorDialog from '/components/action-error-dialog'
import useActionErrorDialog from '/components/use-action-error-dialog'

const fields = [
  { id: "name", label: "Company or trading name", required: true, multiline: false },
  { id: "address", label: "Registered or billing address", required: true, multiline: true },
  { id: "company_number", label: "Company number", required: false, multiline: false },
  { id: "vat_number", label: "VAT number", required: false, multiline: false },
  { id: "billing_email", label: "Billing email", required: true, multiline: false, type: "email" },
  { id: "main_contact_email", label: "Main contact email", required: true, multiline: false, type: "email" },
  { id: "main_contact_phone", label: "Main contact phone", required: true, multiline: false, type: "tel" },
  { id: "other_information", label: "Other information", required: false, multiline: true },
]

const MonthlyPlanApplicationForm = ({ open, setOpen, client, tariff, plans = [] }) => {
  const { apiSource, apiKey, setProcessing, refreshClient } = useContext(AppContext)

  const initialApplication = useMemo(() => ({
    tariff_name: tariff ?? "",
    name: client?.name ?? "",
    address: client?.address ?? "",
    company_number: client?.company_number ?? "",
    vat_number: client?.vat_number ?? "",
    billing_email: client?.billing_email ?? "",
    main_contact_email: client?.main_contact_email ?? "",
    main_contact_phone: client?.main_contact_phone ?? "",
    other_information: "",
  }), [client, tariff])

  const [application, setApplication] = useState(initialApplication)
  const [errors, setErrors] = useState({})
  const [agreed, setAgreed] = useState(false)
  const [termsError, setTermsError] = useState("")
  const [stage, setStage] = useState("form")
  const { actionError, errorDialogOpen, setErrorDialogOpen, showActionError } = useActionErrorDialog()

  useEffect(() => {
    if (!open) {
      setStage("form")
      setApplication(initialApplication)
      setErrors({})
      setAgreed(false)
      setTermsError("")
      return
    }

    if (stage !== "confirmed") {
      setApplication(initialApplication)
      setErrors({})
      setAgreed(false)
      setTermsError("")
    }
  }, [open, initialApplication, stage])

  const close = () => setOpen(false)

  const setField = (field, value) => {
    setApplication(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }))
    }
  }

  const validateFields = () => {
    const nextErrors = {}
    fields.forEach(({ id, required }) => {
      const value = (application[id] ?? "").toString().trim()
      if (required && !value) {
        nextErrors[id] = "This field is required."
      }
    })
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async () => {
    const fieldsValid = validateFields()
    if (!agreed) {
      setTermsError("You must agree to the monthly contract terms and conditions.")
    } else if (termsError) {
      setTermsError("")
    }

    if (!fieldsValid || !agreed) return

    setProcessing(true)

    try {
      const result = await applyForMonthlyPlan({ apiSource, apiKey, application })
      if (result?.error) {
        showActionError(result.error, "Unable to submit your application.")
        return
      }

      await refreshClient()
      setStage("confirmed")
    } catch (err) {
      showActionError(err?.response?.data?.error, "Unable to submit your application.")
    } finally {
      setProcessing(false)
    }
  }

  return (
    <>
    <PopupShell open={open} onClose={close} width={520} variant="solid">
      <div className="flex flex-col gap-5 h-full min-h-0">
        <div className="flex flex-row justify-between">
          <Typography color="secondary" variant="h6">
            {stage === "form"
              ? `Apply for our ${application.tariff_name || tariff} plan`
              : `${application.tariff_name || tariff} plan application submitted`}
          </Typography>
          <Button endIcon={<EastRoundedIcon />} onClick={close}>close</Button>
        </div>

        <hr className="-mt-[10px]" />

        {stage === "form" ? (
          <div className='flex flex-col gap-5 border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg p-6 overflow-y-auto min-h-0 flex-1'>
            <TextField
              className="capitalize"
              select
              label="Requested plan *"
              value={application.tariff_name}
              onChange={(e) => setField("tariff_name", e.target.value)}
              fullWidth
              size='small'
              required
              error={Boolean(errors.tariff_name)}
              helperText={errors.tariff_name ?? " "}
              slotProps={{ inputLabel: { shrink: true } }}
            >
              {plans.map(plan => <MenuItem key={plan.tariff_name} value={plan.tariff_name}>{plan.tariff_name}</MenuItem>)}
            </TextField>

            {fields.map(({ id, label, required, multiline, type }) => (
              <TextField
                key={id}
                label={label + (required ? " *" : "")}
                value={application[id]}
                onChange={(e) => setField(id, e.target.value)}
                fullWidth
                size='small'
                required={required}
                multiline={multiline}
                minRows={multiline ? 4 : undefined}
                type={type}
                error={Boolean(errors[id])}
                helperText={errors[id] ?? " "}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            ))}

            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => {
                      setAgreed(e.target.checked)
                      if (termsError && e.target.checked) setTermsError("")
                    }}
                    color="primary"
                  />
                }
                label={
                  <span>
                    I have read and agree to the{" "}
                    <a
                      href="https://docs.smartix.uk/terms/monthly-terms/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 underline"
                    >
                      monthly contract terms and conditions
                    </a>.
                  </span>
                }
              />
              {termsError ? <Typography color="error" variant="body2">{termsError}</Typography> : null}
            </div>

            <div className='flex flex-row justify-end'>
              <Button variant='contained' onClick={handleSubmit}>Submit application</Button>
            </div>
          </div>
        ) : (
          <div className='flex flex-col gap-5 border border-white/20 dark:border-white/10 bg-white/5 dark:bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg p-6 text-center'>
            <Typography variant='h5'>Application received</Typography>
            <Typography>
              Thanks for submitting your {tariff} plan application. Our team will review it and get back to you shortly.
            </Typography>
            <div className='flex flex-row justify-center'>
              <Button variant='contained' onClick={close}>Close</Button>
            </div>
          </div>
        )}
      </div>
    </PopupShell>
    <ActionErrorDialog title="Application failed" open={errorDialogOpen} setOpen={setErrorDialogOpen} error={actionError} />
    </>
  )
}

export default MonthlyPlanApplicationForm
